import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { initializeAppInsights } from './appInsights';
import { AuthProvider } from './auth/AuthProvider';
import { getMsalInstance } from './auth/msalConfig';
import './index.css';
import reportWebVitals from './reportWebVitals';

const hasMsalResponseParams = () => {
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  // Login: auth code / tokens / error in hash (fragment) or query string
  if (/(#|&)(code|error|id_token|access_token)=/i.test(hash)) return true;
  if (/[?&](code|error|id_token|access_token)=/i.test(search)) return true;
  return false;
};

/**
 * Parse the MSAL state parameter from the URL to detect if this is a popup callback.
 * After cross-origin navigation, window.opener becomes null even in popups,
 * so we need to detect popup context from the MSAL state in the URL.
 *
 * MSAL v5 state format: base64LibraryState|userState
 * where base64LibraryState = URL-safe base64(JSON.stringify({id, meta:{interactionType}}))
 * MSAL uses URL-safe base64: no padding, '-' instead of '+', '_' instead of '/'
 */
const getMsalStateInteractionType = (): string | null => {
  try {
    const urlSearch = window.location.search || '';
    const urlHash = window.location.hash || '';

    let stateStr = '';

    // Check query string first
    const searchParams = new URLSearchParams(urlSearch);
    stateStr = searchParams.get('state') || '';

    // Check hash if not in query string
    if (!stateStr && urlHash) {
      const hashContent = urlHash.startsWith('#') ? urlHash.substring(1) : urlHash;
      const hashParams = new URLSearchParams(hashContent);
      stateStr = hashParams.get('state') || '';
    }

    if (!stateStr) return null;

    // MSAL state may contain multiple parts separated by '|'
    const parts = stateStr.includes('|') ? stateStr.split('|') : [stateStr];

    const tryDecodeBase64Json = (input: string): Record<string, unknown> | null => {
      // Try URL-safe base64 first (MSAL v5: no padding, - and _ chars)
      try {
        const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4);
        return JSON.parse(atob(padded));
      } catch { /* not URL-safe base64 JSON */ }
      // Try standard base64
      try {
        return JSON.parse(atob(input));
      } catch { /* not standard base64 JSON */ }
      // Try direct JSON
      try {
        return JSON.parse(decodeURIComponent(input));
      } catch { /* not JSON */ }
      return null;
    };

    for (const part of parts) {
      if (!part) continue;
      const decoded = tryDecodeBase64Json(part);
      if (decoded?.meta && typeof (decoded.meta as Record<string, unknown>).interactionType === 'string') {
        return (decoded.meta as Record<string, unknown>).interactionType as string;
      }
      if (typeof decoded?.interactionType === 'string') {
        return decoded.interactionType as string;
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Fallback popup detection via localStorage.
 * MSAL writes interaction state to localStorage when loginPopup() is in-flight.
 * Since localStorage is shared across same-origin windows, the popup can read it.
 */
const hasMsalPendingPopupInStorage = (): boolean => {
  try {
    return Object.keys(localStorage).some(key => {
      if (!key.toLowerCase().includes('msal')) return false;
      const val = localStorage.getItem(key);
      if (!val) return false;
      if (val.toLowerCase().includes('popup')) return true;
      try {
        const parsed = JSON.parse(val);
        return (
          parsed?.interactionType === 'popup' ||
          (parsed?.meta?.interactionType === 'popup')
        );
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
};

const isPopupOrIframeContext = () => {
  const hasOpener = !!window.opener && !window.opener.closed;
  const isIframe = window.parent !== window;
  // Detect popup via MSAL state when window.opener is lost after cross-origin redirect
  const isMsalPopupByState = getMsalStateInteractionType() === 'popup';
  // Fallback: detect popup via localStorage (MSAL writes interaction state there)
  const isMsalPopupByStorage = hasMsalResponseParams() && hasMsalPendingPopupInStorage();
  return hasOpener || isIframe || isMsalPopupByState || isMsalPopupByStorage;
};

const runRedirectBridge = async () => {
  const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
  await broadcastResponseToMainFrame();
};

const renderRedirectBridgePage = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    return;
  }

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <div>Completing sign-in...</div>
    </React.StrictMode>
  );
};

const bootstrap = async () => {
  try {
    if (isPopupOrIframeContext() && hasMsalResponseParams()) {
      // Popup context with a login auth response (code/token/error in URL).
      // broadcastResponseToMainFrame() sends the result back to the main window
      // so the loginPopup() promise resolves there.
      renderRedirectBridgePage();
      await runRedirectBridge();
      return;
    }

    const msalInstance = await getMsalInstance();

    // Handle redirect - catch cache errors which can happen in incognito/multi-tab scenarios
    // but don't prevent app initialization since MSAL still functions
    try {
      await msalInstance.handleRedirectPromise({
        hash: window.location.hash,
        navigateToLoginRequestUrl: false, // We'll handle redirect manually
      });
    } catch (redirectError) {
      const errorMessage = (redirectError instanceof Error ? redirectError.message : String(redirectError)).toLowerCase();

      // Log cache-related errors but don't fail - MSAL can still authenticate
      if (errorMessage.includes('no_token_request_cache_error')) {
        console.warn('[MSAL] Cache error during redirect handling - this is safe to ignore', redirectError);
        // Continue anyway - MSAL will still work for authentication
      } else {
        // Other redirect errors should be thrown
        throw redirectError;
      }
    }

    // After redirect promise resolves, check if user is now authenticated
    // If they are and we have response params, redirect to home
    const hasRedirectResponse = hasMsalResponseParams();
    const accountsAfterRedirect = msalInstance.getAllAccounts();

    if (hasRedirectResponse && accountsAfterRedirect.length > 0) {
      console.log('[Bootstrap] User authenticated via redirect, clearing URL params and navigating to app');
      // Clear the redirect params from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    initializeAppInsights();
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );

    root.render(
      <React.StrictMode>
        <AuthProvider instance={msalInstance}>
          <App />
        </AuthProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Failed to initialize app:', error);

    // Only show error page for non-cache errors
    const errorMessage = (error instanceof Error ? error.message : String(error)).toLowerCase();
    if (!errorMessage.includes('no_token_request_cache_error')) {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        const root = ReactDOM.createRoot(rootElement);
        root.render(
          <React.StrictMode>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              backgroundColor: '#f5f5f5',
              padding: '20px'
            }}>
              <h2 style={{ color: '#333' }}>Error Initializing App</h2>
              <p style={{ color: '#666', marginBottom: '20px', maxWidth: '400px', textAlign: 'center' }}>
                An unexpected error occurred during initialization.
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Reload Page
              </button>
            </div>
          </React.StrictMode>
        );
      }
    }
  }
};

bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
